/**
 * Contains Async Actions
 */
import { SM_PROGRAM_ID } from "@constants/app-defaults";
import { axiosInstance } from "@services/axios";
import { client } from "@services/apollo-client";
import { emptyCart as emptyOrderCart, resetCart } from "@slices/cart";

import { fetchRewardUser } from "@stadium/checkout/src/graphql/queries/fetch-reward-points";

import { camelizer } from "@helpers/formatter";
import { formatOrder, checkFetchRewardPointsRequired } from "@stadium/checkout/src/helpers/order";
import { formatRecipientOrder } from "@helpers/orders/cart";
import { formatRlpOrder } from "@helpers/orders/rlp";
import { clearOrder } from "@slices/order";
import { jsonDataFormatter } from "@stadium/helpers/formatter";
import { handleError } from "@stadium/helpers/error-handler";

import { UPDATE_ORDER_ERROR_TOAST_ID } from "@constants/toast-ids";
import { withSkipContacts } from "@stadium/helpers/form/utils";

const { get, patch, post, put } = axiosInstance;

export const createOrder = (payload) => async () =>
  post("/orders", withSkipContacts({ order: payload }))
    .then((res) => {
      const responseData = jsonDataFormatter(res);
      return formatOrder(responseData);
    })
    .catch((error) => {
      throw error;
    });

export const populateOrder = (orderId, payload) => async () =>
  post(`/orders/${orderId}/populate_for_eshop`, payload)
    .then((res) => {
      const responseData = jsonDataFormatter(res);
      return formatOrder(responseData);
    })
    .catch((error) => {
      throw error;
    });

export const batchPopulateOrder = (orderId, payload) => async () =>
  patch(`/orders/${orderId}/batch_populate_for_eshop`, withSkipContacts(payload))
    .then((res) => {
      const responseData = jsonDataFormatter(res);
      return formatOrder(responseData);
    })
    .catch((error) => {
      throw error;
    });

export const emptyCart =
  (orderId, payload = {}) =>
  async (dispatch) =>
    patch(`/orders/${orderId}/empty_for_eshop`, payload)
      .then((res) => {
        dispatch(emptyOrderCart());
        const responseData = jsonDataFormatter(res);
        return formatOrder(responseData);
      })
      .catch((error) => {
        throw error;
      });

export const cumulativeCart = (orderId) => async () =>
  get(`/orders/${orderId}/cumulative_cart`)
    .then((res) => {
      const responseData = jsonDataFormatter(res);
      return formatOrder(responseData);
    })
    .catch((error) => {
      throw error;
    });

export const showOrderRlp =
  (orderId, params = {}) =>
  async () =>
    get(`/orders/${orderId}/show_rlp`, params)
      .then((res) => {
        const responseData = jsonDataFormatter(res);
        return formatRlpOrder(responseData);
      })
      .catch((error) => {
        throw error;
      });

export const validateOrderContactEmail = (orderNumber, params) => async () =>
  get(`/orders/${orderNumber}/validate_email`, params)
    .then((res) => res)
    .catch((error) => {
      throw error;
    });

export const generateOrderInstance = (orderId) => async () =>
  post(`/orders/${orderId}/generate_order_instance`)
    .then((res) => {
      const responseData = jsonDataFormatter(res);
      return formatRecipientOrder(responseData);
    })
    .catch((error) => {
      throw error;
    });

export const updateOrderDetails =
  (orderId, payload, secondaryPayload = {}, responseType = "") =>
  async () =>
    patch(
      `/orders/${orderId}`,
      withSkipContacts({
        order: { ...payload, ...secondaryPayload },
        ...(responseType && { response_type: responseType }),
      }),
    )
      .then((res) => {
        const responseData = jsonDataFormatter(res);
        return responseData;
      })
      .catch((error) => {
        handleError(error, UPDATE_ORDER_ERROR_TOAST_ID);
      });

export const createPaymentIntent = async (number, payload) =>
  post(`/orders/${number}/create_payment_intent`, payload).then((data) => camelizer(data));

export const refreshTotals = (orderId) => async () =>
  patch(
    `/orders/${orderId}/refresh_totals`,
    withSkipContacts({
      response_type: "cumulative_cart",
      program_id: SM_PROGRAM_ID,
    }),
  )
    .then((res) => {
      const responseData = jsonDataFormatter(res);
      const orderData = formatOrder(responseData);
      return orderData;
    })
    .catch((error) => {
      throw error;
    });

export const fetchStadiumPoints = () => async () => {
  const response = await client.query({
    query: fetchRewardUser,
    fetchPolicy: "network-only",
  });
  return response;
};

export const checkout =
  (
    orderId,
    payload,
    { clearOrderRequired = true, fetchRewardPoints = false, successCb = () => {} } = {},
  ) =>
  async (dispatch) =>
    put(`/orders/${orderId}/checkout`, payload)
      .then((response) => {
        if (fetchRewardPoints || checkFetchRewardPointsRequired(response))
          dispatch(fetchStadiumPoints());
        return successCb();
      })
      .then(() => {
        if (clearOrderRequired) {
          dispatch(clearOrder());
          dispatch(resetCart());
        }
      });

export const fetchEShopOrder = (number) =>
  get(`/orders/${number}/details`, withSkipContacts({})).then((res) => {
    const responseData = jsonDataFormatter(res);
    const orderData = formatOrder(responseData);
    return orderData;
  });
